import { useEffect, useState } from 'react';
import { Box, Tabs, Tab, RadioGroup, FormControlLabel, Radio, Button, Tooltip, Typography, IconButton, Accordion, AccordionSummary, AccordionDetails, CircularProgress, Divider, Table, TableHead, TableContainer, TableRow, TableCell, TableBody, Card, CardContent, Chip, Stack, Link, TextField, } from '@mui/material';
import { InfoOutlined, CheckCircle, Add, ExpandMore, Edit } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import Header from 'components/Header';
import { useSelector } from 'react-redux';
import { noteSearch, ratingSave, artifactSearch, goalRatingSearch, save as saveEvaluation, ratingSearch } from 'store/evaluation/admin-evaluation';
import { useDispatch } from 'react-redux';
import { BasePathProvider, useBasePath, useSession } from 'components/Providers';
import Confirm from 'components/Dialog';
import { useTranslation } from 'react-i18next';
import NoteDrawer from './AdminEvaluateNoteForm';
import * as snackbar from '../../../../utils/snackbar'


export default function AdminEvaluateForm({ item }) {
    const [currentTab, setCurrentTab] = useState(0);
    const dispatch = useDispatch();
    const basePath = useBasePath();
    const [confirm, setConfirm] = useState(false);
    const [pendingTab, setPendingTab] = useState(null);
    const adminRating = useSelector(state => state.adminEvaluation.evaluation.adminRatingList);
    const goalRating = useSelector(state => state.adminEvaluation.evaluation.goalRatingList);
    const notes = useSelector(state => state.adminEvaluation.evaluation.noteList);
    const { person } = useSession()

    const { t } = useTranslation();
    const evaluationId = item?.evaluation?.id || null;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const adminStandards = useSelector(state => state.lookup.adminStandards?.filter(item => item.type === 1));

    const standards = adminStandards.map((item, index) => ({
        id: item.id,
        label: `Standard ${index + 1}`
    }));
    standards.push({ id: 7, label: 'Standard 7' });
    standards.push({ id: 9, label: 'Finalize' });
    const standardId = standards[currentTab].id;

    const goalStandard = useSelector(state => state.lookup.adminStandards?.filter(item => item.type === 2));

    useEffect(() => {
        if (evaluationId) {
            dispatch(ratingSearch({ evaluationId }));
            dispatch(artifactSearch({ evaluationId }));
            dispatch(goalRatingSearch({ evaluationId }));
        }
    }, [dispatch, evaluationId]);

    useEffect(() => {
        dispatch(noteSearch({ evaluationId, standardId }));
    }, [dispatch, evaluationId, standardId]);

    const artifactList = useSelector(state =>
        state?.adminEvaluation?.evaluation?.artifactList?.filter(artifact => {
            const standardIds = artifact?.indicatorIds?.map(indicator => parseInt(indicator.split('.')[0], 10));
            return standardIds.includes(standards[currentTab].id);
        }).map(artifact => ({
            ...artifact,
            indicatorIds: artifact.indicatorIds.filter(indicator => {
                const standardId = parseInt(indicator.split('.')[0], 10);
                return standardId === standards[currentTab].id;
            }).map(indicator => `${standards[currentTab].id}.${indicator.split('.')[1]}`)
        }))
    );

    const merged = standards.map(std => {
        const match = adminRating?.find(r => r.id === std.id);
        return {
            ...std,
            evaluation: match ? match.adminEvaluationRatings : [],
        };
    });

    const activeEval = merged[currentTab]?.evaluation?.find(item => !item.isSelf) || null;
    const eval7 = goalRating?.find(r => r.id === 7)?.adminEvaluationRatings?.[0] || null;
    const adminGoalDetails7 = goalRating?.find(r => r.id === 7)?.adminEvaluationGoal || [];
    const eval8 = goalRating?.find(r => r.id === 8)?.adminEvaluationRatings?.[0] || null;
    const adminGoalDetails8 = goalRating?.find(r => r.id === 8)?.adminEvaluationGoal || [];
    const adminSelf = merged[currentTab]?.evaluation?.find(item => item.isSelf) || null;

    useEffect(() => {
        if (activeEval || eval7 || eval8) {
            reset({
                rubricId: activeEval?.rubricId || eval7?.rubricId,
                rubricId2: eval8?.rubricId,
                finalFeedback: item?.evaluation?.finalFeedback,
            });
        } else {
            reset({
                rubricId: '',
                rubricId2: '',
                finalFeedback: '',
            });
        }
    }, [currentTab, activeEval]);

    const { control, handleSubmit, reset, formState: { isDirty, isSubmitting } } = useForm({
        defaultValues: {
            rubricId: activeEval?.rubricId || eval7?.rubricId || '',
            rubricId2: eval8?.rubricId || '',
            finalFeedback: item?.evaluation?.finalFeedback || '',
        },
    });

    const onSubmit = async (data) => {

        if (currentTab === 6) {
            const payload7 = {
                rubricId: parseInt(data.rubricId),
                ...((eval7?.id) ? { id: eval7.id } : {
                    evaluationId: item?.evaluation?.id,
                    standardId: 7
                }),
            }

            const payload8 = {
                rubricId: parseInt(data.rubricId2),
                ...(eval8?.id ? { id: eval8.id } : {
                    evaluationId: item?.evaluation?.id,
                    standardId: 8
                }),
            }

            await dispatch(ratingSave(payload7));
            await dispatch(ratingSave(payload8));

        } else if (currentTab === 7) {
            await dispatch(saveEvaluation({ id: item?.evaluation?.id, isFinalized: true, finalFeedback: data.finalFeedback }));
        } else {

            const payload = {
                rubricId: parseInt(data.rubricId),
                ...(activeEval?.id ? { id: activeEval.id } : {
                    evaluationId: item?.evaluation?.id,
                    standardId: standards[currentTab].id
                }),
            };

            await dispatch(ratingSave(payload));
        }

    };

    const handleTabChange = (_, newIdx) => {
        if (newIdx === currentTab) return;

        if (isDirty) {
            setPendingTab(newIdx);
            setConfirm(true);
        } else {
            setCurrentTab(newIdx);
        }
    };

    const handleConfirm = () => {
        if (pendingTab !== null) {
            setCurrentTab(pendingTab);
            setPendingTab(null);
        }
        setConfirm(false);
    };

    const allRated = Array.isArray(adminRating) &&
        adminRating.every(std =>
            Array.isArray(std.adminEvaluationRatings) &&
            std.adminEvaluationRatings.some(r => !r.isSelf)
        );

    const allGoalsRated = Array.isArray(goalRating) &&
        goalRating.every(goal =>
            Array.isArray(goal.adminEvaluationRatings) &&
            goal.adminEvaluationRatings.some(r => !r.isSelf)
        );

    return (
        <BasePathProvider value={`${basePath}/evaluation`}>
            <Header title={'Evaluation'} small close borderBottom />
            <Tabs value={currentTab} onChange={handleTabChange} variant='scrollable' sx={{ borderBottom: 1, borderColor: 'divider', pt: 1, overflowX: 'auto' }}>
                {standards.map((s) => (
                    <Tab key={s.id} label={s.label} />
                ))}
            </Tabs>
            {adminStandards.some(standard => standard.id === standardId) && (

                <Box mt={1} p={2}>
                    <Typography variant="body1" fontWeight={500}>{`Effectiveness in ${adminStandards[currentTab]?.name}`} </Typography>
                    <Controller
                        name="rubricId"
                        control={control}
                        render={({ field }) => (
                            <RadioGroup {...field}>
                                {adminStandards[currentTab]?.adminEvaluationRubrics?.map((item) => (
                                    <FormControlLabel
                                        key={item.score}
                                        value={item.id}
                                        control={<Radio />}
                                        label={
                                            <Box display="flex" alignItems="center">
                                                <Typography>{item.performance}</Typography>
                                                <Tooltip title={item.description || ''} placement='right' arrow >
                                                    <IconButton size="small" sx={{ ml: 0.5 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {adminSelf && adminSelf.rubricId === item.id && (
                                                    <Tooltip title={`Admin's Self-Rating`} placement='right' arrow >
                                                        <IconButton size="small" sx={{ color: 'green' }}>
                                                            <CheckCircle fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    />

                    <Box mt={3} textAlign="right">
                        <Button
                            variant="contained"
                            onClick={handleSubmit(async (data) => {
                                await onSubmit(data);
                                reset(data);
                            })}
                            disabled={isSubmitting || !isDirty}
                            startIcon={isSubmitting && <CircularProgress size={20} />}
                        >
                            {t("Save")}
                        </Button>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <TableContainer>

                        <Button
                            variant='outlined'
                            size='small'
                            sx={{ float: 'right' }}
                            startIcon={<Add />}
                            onClick={() => setDrawerOpen(true)}
                        >
                            Add Note
                        </Button>

                        <Table size='small'>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={'80%'}>Notes</TableCell>
                                    <TableCell width={'20%'}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {notes && notes.length > 0 ? (
                                    notes.map((item) => (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {item.note.length > 85 ? `${item.note.substring(0, 85)}...` : item.note}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => {
                                                    setDrawerOpen(true);
                                                    setSelectedNote(item);
                                                }}>
                                                    <Edit />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            <Typography variant="body2" color="textSecondary">No notes available</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box mt={3}>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant='body1'>Artifact List</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box display="flex" flexDirection={'column'} gap={1}>
                                    {artifactList?.map((art) => (
                                        <Card key={art.id} variant="outlined">
                                            <CardContent>
                                                <Box display="flex" flexDirection="row" flextWrap='wrap' justifyContent="flex-end" mb={2}>
                                                    {art.file ?
                                                        <Chip label={'View Attachment'} href={art?.file?.location} component="a" clickable target="_blank" rel="noopener noreferrer" size="small" /> :
                                                        <Chip label={'No Attachment'} color="warning" size="small" />
                                                    }
                                                </Box>
                                                <Typography variant="body2">{art?.explanation}</Typography>
                                                <Box display="flex" flexDirection="row" flextWrap='wrap' justifyContent="flex-end" gap={1} mt={2}>
                                                    {art?.indicatorIds.map((indicator, index) => (
                                                        <Chip key={index} label={indicator} size="small" />
                                                    ))}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
            )}

            {standardId === 7 && (

                <Box mt={1} p={2}>
                    <Typography variant="body1" fontWeight={500}>{`${goalStandard[0]?.name}`} </Typography>
                    <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography>{`${goalStandard[0]?.name} Details`}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box display="flex" flexDirection="column" gap="5px" border={1} borderColor="divider" p={1} mb={2} borderRadius={3}>
                                    <Stack direction="column">
                                        <Typography variant="body2" color="textSecondary">
                                            Goal Description
                                        </Typography>
                                        {adminGoalDetails7?.goal ? (
                                            <Typography variant="body1" >
                                                {adminGoalDetails7?.goal}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body1">No Goal Set Yet</Typography>
                                        )}
                                    </Stack>
                                    <Stack direction="column">
                                        <Typography variant="body2" color="textSecondary">
                                            Uploaded File
                                        </Typography>
                                        {adminGoalDetails7?.file ? (
                                            <Link href={adminGoalDetails7.file.location} target="_blank">{adminGoalDetails7.file.name}</Link>
                                        ) : (
                                            <Typography variant="body1">No file available</Typography>
                                        )}
                                    </Stack>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    <Controller
                        name="rubricId"
                        control={control}
                        render={({ field }) => (
                            <RadioGroup {...field}>
                                {goalStandard[0]?.adminEvaluationRubrics?.map((item) => (
                                    <FormControlLabel
                                        key={item.score}
                                        value={item.id}
                                        control={<Radio />}
                                        label={
                                            <Box display="flex" alignItems="center">
                                                <Typography>{item.performance}</Typography>
                                                <Tooltip title={item.description || ''} placement='right' arrow >
                                                    <IconButton size="small" sx={{ ml: 0.5 }}>
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    />

                    <Box>
                        <Typography variant="body1" fontWeight={500} sx={{ pt: 3 }}>{`${goalStandard[1]?.name} Effectiveness`} </Typography>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography>{`${goalStandard[1]?.name} Details`}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box display="flex" flexDirection="column" gap="5px" border={1} borderColor="divider" p={1} mb={2} borderRadius={3}>
                                    <Stack direction="column">
                                        <Typography variant="body2" color="textSecondary">
                                            Goal Description
                                        </Typography>
                                        {adminGoalDetails8?.goal ? (
                                            <Typography variant="body1" >
                                                {adminGoalDetails8?.goal}
                                            </Typography>
                                        ) : (
                                            <Typography variant="body1">No Goal Set Yet</Typography>
                                        )}
                                    </Stack>
                                    <Stack direction="column">
                                        <Typography variant="body2" color="textSecondary">
                                            Uploaded File
                                        </Typography>
                                        {adminGoalDetails8?.file ? (
                                            <Link href={adminGoalDetails8.file.location} target="_blank">{adminGoalDetails8.file.name}</Link>
                                        ) : (
                                            <Typography variant="body1">No file available</Typography>
                                        )}
                                    </Stack>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                        <Controller
                            name="rubricId2"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup {...field}>
                                    {goalStandard[1]?.adminEvaluationRubrics?.map((item) => (
                                        <FormControlLabel
                                            key={item.score}
                                            value={item.id}
                                            control={<Radio defaultValue={eval8?.rubricId || ''} />}
                                            label={
                                                <Box display="flex" alignItems="center">
                                                    <Typography>{item.performance}</Typography>
                                                    <Tooltip title={item.description || ''} placement='right' arrow >
                                                        <IconButton size="small" sx={{ ml: 0.5 }}>
                                                            <InfoOutlined fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                            )}
                        />
                    </Box>

                    <Box mt={3} textAlign="right">
                        <Button
                            variant="contained"
                            onClick={handleSubmit(async (data) => {
                                await onSubmit(data);
                                reset(data);
                            })}
                            disabled={isSubmitting || !isDirty}
                            startIcon={isSubmitting && <CircularProgress size={20} />}
                        >
                            {t("Save")}
                        </Button>
                    </Box>
                </Box>
            )}

            {standardId === 9 && (

                <Box mt={1} p={2}>
                    <Typography variant="body1" fontWeight={500}>{`Finalize ${item?.firstName} ${item.lastName}'s Evaluation`} </Typography>
                    {adminRating?.length > 0 && (
                        <Box >
                            <Typography variant="body1" color="textSecondary">
                                Please review the evaluation details below before finalizing.
                            </Typography>
                            <Box mt={1}>
                                {adminRating.map((std, idx) => (
                                    <Box key={std.id || idx} mb={1}>
                                        <Typography variant="body1" color="textSecondary" fontWeight={500} >
                                            {`Standard ${std.id}`}
                                        </Typography>
                                        {std.adminEvaluationRatings.length > 0 ? (
                                            std.adminEvaluationRatings
                                                .filter(r => !r.isSelf)
                                                .map(rating => (
                                                    <Box
                                                        key={rating.id}
                                                        display="flex"
                                                    >
                                                        <Typography variant="body1">
                                                             {`Score: ${rating.adminEvaluationRubric.score}`} - {rating.adminEvaluationRubric.performance}
                                                        </Typography>
                                                    </Box>
                                                ))
                                        ) : (
                                            <Typography variant="body2" color="error">No Rating Yet</Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    <Box  mb={1}>
                        <Typography variant="body1" fontWeight={500}  color='textSecondary'>
                            Standard 7 iReady Goal
                        </Typography>
                        <Box>
                            {goalRating[0]?.adminEvaluationRatings.length > 0 ? (
                                <Typography variant="body1">
                                    {`Score: ${goalRating[0]?.adminEvaluationRatings[0].adminEvaluationRubric.score}`} - {goalRating[0]?.adminEvaluationRatings[0].adminEvaluationRubric.performance}
                                </Typography>) : (
                                <Typography variant="body2" color="error">No Rating Yet</Typography>
                            )
                            }
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="body1" color="textSecondary" fontWeight={500}>
                            Standard 7 Admin Goal
                        </Typography>
                        <Box>
                            {goalRating[1]?.adminEvaluationRatings.length > 0 ? (
                                <Typography variant="body1">
                                    {`Score: ${goalRating[1]?.adminEvaluationRatings[0].adminEvaluationRubric.score}`} - {goalRating[1]?.adminEvaluationRatings[0].adminEvaluationRubric.performance}
                                </Typography>) : (
                                <Typography variant="body2" color="error">No Rating Yet</Typography>
                            )
                            }
                        </Box>
                    </Box>
                    <Box mt={2}>
                        <Controller
                            name="finalFeedback"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Final Feedback"
                                    multiline
                                    fullWidth
                                    variant="outlined"
                                    rows={4}
                                    error={Boolean(field.error)}
                                    helperText={field.error ? field.error.message : ''}
                                    size='small'
                                />
                            )}
                        />

                    </Box>
                    <Box mt={2} display="flex" gap={2} textAlign="right">
                        <TextField
                            label="Signature"
                            value={`${person?.firstName || ''} ${person?.lastName || ''}`}
                            InputProps={{
                                readOnly: true,
                            }}
                            fullWidth
                            size='small'
                        />
                        <Button
                            variant="contained"
                            onClick={handleSubmit(async (data) => {
                                if (!allRated || !allGoalsRated) {
                                    snackbar.error('All standards must be rated before finalizing the evaluation.');
                                    return;
                                }
                                await onSubmit(data);
                                reset(data);
                            })}
                            disabled={isSubmitting}
                            startIcon={isSubmitting && <CircularProgress size={20} />}
                        >
                            {t("Finalize")}
                        </Button>
                    </Box>
                </Box>
            )}

            <Confirm open={confirm} onClose={() => setConfirm(false)} onConfirm={handleConfirm} message="You have unsaved changes. Are you sure you want to discard changes?" />
            <NoteDrawer open={drawerOpen} onClose={() => { setDrawerOpen(false); setSelectedNote(null); }} evaluationId={evaluationId} standardId={standardId} setSelectedNote={setSelectedNote} selectedNote={selectedNote} />
        </BasePathProvider>
    );
}
